"use strict";

import _objectSpread from "@babel/runtime/helpers/objectSpread";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { Messages } from "Messages";
import { showSnackBar } from "Actions/D5SnackbarsActions";
import { Fields, System } from "Constants/objects";
import { createGuid } from "D5Utils/functions";
import UserScriptFormFilterModel from "UserActionHandler/UserScriptFormFilterModel";
/**
 * @module UserScriptTable
 * @exports UserScriptTable
 */

/**
 * Class uses to manage table data in user defined script.
 */

export var UserScriptTable =
/*#__PURE__*/
function () {
  /**
   * Count of table rows, that's loading on a page.
   * @type {number}
   * @private
   */

  /**
   * Key field of a table. Required.
   * @type {string}
   * @private
   */

  /**
   * Keys of deleted rows.
   * @type {Array}
   * @private
   */

  /**
   * Keys of updated rows.
   * @type {Array}
   * @private
   */

  /**
   * Keys of inserted rows.
   * @type {Array}
   * @private
   */

  /**
   * @type {string}
   * @private
   */

  /**
   * @type {boolean}
   * @private
   */

  /** Table rows, where each element of the array is an object. The object's field is `FormFields.Name`
   * @type {array}
   * @example
   * //SomeFormEdit.js
   * SomeFieldChanged(model) {
   *     let TableFld = model.getFieldByName(TableField);
   *
   *     console.log(TableFld.data); //array
   *
   *     return model;
   * }
   */

  /**Filters for a request, the same as in our API
   * @type {object}
   * @example
   * //SomeFormEdit.js
   * SomeFieldChanged(model) {
   *     let TableFld = model.getFieldByName(TableField);
   *
   *     console.log(TableFld.filter);
   *
   *     return model;
   * }
   */

  /**Parameters of sort for a request, the same as in our API
   * @type {array}
   * @example
   * //SomeFormEdit.js
   * SomeFieldChanged(model) {
   *     let TableFld = model.getFieldByName(TableField);
   *
   *     console.log(TableFld.sorts); //array
   *
   *     return model;
   * }
   */

  /**
   * @param {number} [pageSize] Count of table rows, that's loading on a page.
   * @param {array} [data=[]] Table rows, where each element of the array is an object. The object's field is `FormFields.Name`
   * @param {object} [filters={}] Filters for a request, the same as in our API
   * @param {array} [sorts=[]] Parameters of sort for a request, the same as in our API
   * @param {string} keyField Key field of a table. Required.
   * @param {array} columns Required.
   * @param {string} [parentForm] Name of the parent form.
   * @param {boolean} [isNested=false]
   * @param {number} formID
   * @param {object} filter
   *
   * @hideconstructor
   */
  function UserScriptTable(_ref) {
    var pageSize = _ref.pageSize,
        _ref$data = _ref.data,
        data = _ref$data === void 0 ? [] : _ref$data,
        _ref$filters = _ref.filters,
        filters = _ref$filters === void 0 ? {} : _ref$filters,
        _ref$sorts = _ref.sorts,
        sorts = _ref$sorts === void 0 ? [] : _ref$sorts,
        keyField = _ref.keyField,
        columns = _ref.columns,
        parentForm = _ref.parentForm,
        _ref$isNested = _ref.isNested,
        isNested = _ref$isNested === void 0 ? false : _ref$isNested,
        formID = _ref.formID,
        _ref$filter = _ref.filter,
        filter = _ref$filter === void 0 ? {} : _ref$filter;

    _classCallCheck(this, UserScriptTable);

    _defineProperty(this, "_pageSize", void 0);

    _defineProperty(this, "_keyField", void 0);

    _defineProperty(this, "_deleted", []);

    _defineProperty(this, "_updated", []);

    _defineProperty(this, "_inserted", []);

    _defineProperty(this, "_parentForm", void 0);

    _defineProperty(this, "_isNested", void 0);

    _defineProperty(this, "data", void 0);

    _defineProperty(this, "filters", void 0);

    _defineProperty(this, "sorts", void 0);

    _defineProperty(this, "columns", void 0);

    if (!keyField) throw new Error('`keyField` is required');
    if (!columns || !columns.length) throw new Error('`columns` is required');

    if (filter) {
      filter.filterModel = new UserScriptFormFilterModel(filter);
      this._filterModel = filter.filterModel;
    }

    this._pageSize = pageSize;
    this.data = data;
    this.filters = filters;
    this.sorts = sorts;
    this._keyField = keyField;
    this.columns = columns;
    this._parentForm = parentForm;
    this._isNested = isNested;
    this._formID = formID;
  }

  _createClass(UserScriptTable, [{
    key: "_duplicateDicFieldsData",
    value: function _duplicateDicFieldsData(data) {
      for (var d in data) {
        //если это справочник, то в пользовательских скриптах мы пишем значение по нейму
        // и это значение нужно скопировать в системной поле чтобы сработал лукап
        if (data.hasOwnProperty(d) && d.indexOf(System.SYSTEM_PREFIX) !== 0 && this.isDictionary(d)) {
          data[System.SYSTEM_PREFIX + d] = data[d];
        }
      }
    }
  }, {
    key: "_getColByName",
    value: function _getColByName(name) {
      return this.columns.find(function (col) {
        return col.Name === name;
      });
    }
    /**Key field name of the table.
     * @type {string}
     * @example
     * //SomeFormEdit.js
     * SomeFieldChanged(model) {
     *     let TableFld = model.getFieldByName(TableField);
     *
     *     console.log(TableFld.keyField);
     *
     *     return model;
     * }
     */

  }, {
    key: "addRow",

    /**
     * Adds a row in a table.
     * @param {object} data
     * @return {string}
     */
    value: function addRow() {
      var data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var key = createGuid();
      data[System.SYSTEM_PREFIX + this._keyField] = key;

      this._duplicateDicFieldsData(data);

      this.data.push(data);

      this._inserted.push(key);

      return key;
    }
    /**
     * Deletes a row by key
     * @param {(string|number)} key
     */

  }, {
    key: "deleteRow",
    value: function deleteRow(key) {
      var index = this.getRowIndex(key);
      this.data.splice(index, 1);
    }
    /**
     * Clears all the rows.
     */

  }, {
    key: "clear",
    value: function clear() {
      this.data.length = 0;
    }
    /**Shows whether the field is a dictionary or not.
     * @param {String} name
     * @return {boolean}
     */

  }, {
    key: "isDictionary",
    value: function isDictionary(name) {
      var view = this._getColByName(name);

      if (!name || !view) throw new Error("The table does not have the field - \"".concat(name, "\""));
      return view[Fields.ObjectFieldID_LinkedObjectID] != null;
    }
    /**
     * Edits a row by key;
     * @param {(string|number)} key
     * @param {object} data
     */

  }, {
    key: "editRow",
    value: function editRow(key, data) {
      var rowData = this.data[this.getRowIndex(key)];

      this._duplicateDicFieldsData(data);

      this.data[this.getRowIndex(key)] = _objectSpread({}, rowData, data);
    }
    /**
     * Returns row data by key;
     * @param {(string|number)} key
     * @return {(object|undefined)}
     */

  }, {
    key: "getRow",
    value: function getRow(key) {
      return this.data[this.getRowIndex(key)];
    }
    /**
     * Returns row index by key;
     * @param {(string|number)} key
     * @return {number} Index of a row or -1 if the row doesn't exist.
     */

  }, {
    key: "getRowIndex",
    value: function getRowIndex(key) {
      var _this = this;

      return this.data.findIndex(function (row) {
        return row[_this._keyField] === key;
      });
    }
  }, {
    key: "getFilterByName",
    value: function getFilterByName(name) {
      if (this._filterModel) {
        return this._filterModel.getFilterByName(name);
      }
    }
  }, {
    key: "keyField",
    get: function get() {
      return this._keyField;
    }
    /**Parent form name.
     * @type {string}
     * @example
     * //SomeFormEdit.js
     * SomeFieldChanged(model) {
     *     let TableFld = model.getFieldByName(TableField);
     *
     *     console.log(TableFld.parentForm);
     *
     *     return model;
     * }
     */

  }, {
    key: "parentForm",
    get: function get() {
      return this._parentForm;
    }
    /**Value is `true` if the table is nested.
     * @type {boolean}
     * @example
     * //SomeFormEdit.js
     * SomeFieldChanged(model) {
     *     let TableFld = model.getFieldByName(TableField);
     *
     *     console.log(TableFld.isNested);
     *
     *     return model;
     * }
     */

  }, {
    key: "isNested",
    get: function get() {
      return this._isNested;
    }
  }, {
    key: "pageSize",
    get: function get() {
      return this._pageSize;
    },
    set: function set(size) {
      if (size < 1) {
        showSnackBar({
          type: System.SNACKBAR_TYPES.Error,
          message: Messages.Error.PageSize
        });
        return;
      }

      this._pageSize = size;
    }
    /** Form ID of the table.
     * @type {number}
     * @example
     * //SomeFormEdit.js
     * SomeFieldChanged(model) {
     *     let TableFld = model.getFieldByName(TableField);
     *
     *     console.log(TableFld.formID);
     *
     *     return model;
     * }
     */

  }, {
    key: "formID",
    get: function get() {
      return this._formID;
    }
  }]);

  return UserScriptTable;
}();